import type { CSSProperties } from 'react';
import { vars } from '../../theme/contract.css';
import type { BoxProps } from './box.types';

const shadowStyles: Record<NonNullable<BoxProps['shadow']>, string> = {
    soft: '0 1px 6px rgba(0, 0, 0, 0.35)',
    card: '0 2px 4px rgba(0, 0, 0, 0.4)',
    overlay: '0 4px 16px rgba(0, 0, 0, 0.5)',
};

const space = (scale?: string) => (scale != null ? vars.space[scale] : undefined);
const radius = (r?: string) =>
    r == null ? undefined : r === 'full' ? '9999px' : vars.radius[r];

export function Box({
    children,
    as: Element = 'div',
    id,
    background,
    borderColor,
    borderStyle = 'solid',
    borderRadius,
    borderStartStartRadius,
    borderStartEndRadius,
    borderEndStartRadius,
    borderEndEndRadius,
    borderWidth,
    borderBlockStartWidth,
    borderBlockEndWidth,
    borderInlineStartWidth,
    borderInlineEndWidth,
    minHeight,
    minWidth,
    maxWidth,
    width,
    height,
    overflow,
    overflowX,
    overflowY,
    padding,
    paddingBlock,
    paddingBlockStart,
    paddingBlockEnd,
    paddingInline,
    paddingInlineStart,
    paddingInlineEnd,
    position,
    insetBlockStart,
    insetBlockEnd,
    insetInlineStart,
    insetInlineEnd,
    opacity,
    shadow,
    zIndex,
    flex,
    onPress,
    accessibilityLabel,
    testID,
}: BoxProps) {
    const paddingTop = paddingBlockStart ?? paddingBlock ?? padding;
    const paddingBottom = paddingBlockEnd ?? paddingBlock ?? padding;
    const paddingLeft = paddingInlineStart ?? paddingInline ?? padding;
    const paddingRight = paddingInlineEnd ?? paddingInline ?? padding;

    const anyBorderWidth =
        borderWidth ??
        borderBlockStartWidth ??
        borderBlockEndWidth ??
        borderInlineStartWidth ??
        borderInlineEndWidth;

    const style: CSSProperties = {
        boxSizing: 'border-box',
        flex,
        opacity,
        zIndex,
        position,
        width,
        height,
        minHeight,
        minWidth,
        maxWidth,
        overflow,
        overflowX,
        overflowY,
        backgroundColor: background ? vars.color[background] : undefined,
        borderStyle: anyBorderWidth ? borderStyle : undefined,
        borderColor: borderColor
            ? borderColor === 'transparent'
                ? 'transparent'
                : vars.color[borderColor]
            : undefined,
        // Sides without an explicit width must be 0: `border-style` is applied to
        // all four sides, and CSS would otherwise give them the default `medium`.
        borderTopWidth: anyBorderWidth ? space(borderBlockStartWidth ?? borderWidth ?? '0') : undefined,
        borderBottomWidth: anyBorderWidth ? space(borderBlockEndWidth ?? borderWidth ?? '0') : undefined,
        borderLeftWidth: anyBorderWidth ? space(borderInlineStartWidth ?? borderWidth ?? '0') : undefined,
        borderRightWidth: anyBorderWidth ? space(borderInlineEndWidth ?? borderWidth ?? '0') : undefined,
        borderRadius: radius(borderRadius),
        borderStartStartRadius: radius(borderStartStartRadius),
        borderStartEndRadius: radius(borderStartEndRadius),
        borderEndStartRadius: radius(borderEndStartRadius),
        borderEndEndRadius: radius(borderEndEndRadius),
        paddingTop: space(paddingTop),
        paddingBottom: space(paddingBottom),
        paddingLeft: space(paddingLeft),
        paddingRight: space(paddingRight),
        insetBlockStart: space(insetBlockStart),
        insetBlockEnd: space(insetBlockEnd),
        insetInlineStart: space(insetInlineStart),
        insetInlineEnd: space(insetInlineEnd),
        boxShadow: shadow ? shadowStyles[shadow] : undefined,
        cursor: onPress ? 'pointer' : undefined,
    };

    const interactiveProps = onPress
        ? {
              onClick: onPress,
              role: 'button' as const,
              tabIndex: 0,
              'aria-label': accessibilityLabel,
              onKeyDown: (e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      void onPress();
                  }
              },
          }
        : {};

    return (
        <Element id={id} data-testid={testID} style={style} {...interactiveProps}>
            {children}
        </Element>
    );
}
